import { ComponentRef } from "@angular/core";
import { Subscription } from "rxjs";
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { TourConfig } from "./models/tour-config.model";
import {
  TourButtonConfig,
  TourStepConfig,
  TourStepPosition,
} from "./models/tour-step.model";
import { TourOverlayComponent } from "./components/tour-overlay/tour-overlay.component";
import { TourTooltipComponent } from "./components/tour-tooltip/tour-tooltip.component";

export class TourCdk {
  private currentStepIndex = 0;
  private overlayRefBackdrop: OverlayRef | null = null;
  private overlayRefTooltip: OverlayRef | null = null;
  private tooltipSubscription: Subscription | null = null;
  private tooltipPositionChangesSubscription: Subscription | null = null;
  private tooltipResizeObserver: ResizeObserver | null = null;
  private tooltipLayoutRefreshRaf: number | null = null;
  private isActive = false;
  private currentTargetRect: DOMRect | null = null;

  constructor(
    private readonly config: TourConfig,
    private readonly overlay: Overlay,
    private readonly positionBuilder: OverlayPositionBuilder,
  ) {}

  start(): void {
    if (this.isActive) {
      return;
    }

    this.currentStepIndex = 0;
    this.isActive = true;
    void this.showCurrentStep();
  }

  startFromStepId(stepId: string): void {
    if (this.isActive) {
      return;
    }

    const stepIndex = this.config.steps.findIndex((step) => step.id === stepId);

    if (stepIndex === -1) {
      // If the step is not found, start from the beginning
      this.start();
      return;
    }

    this.currentStepIndex = stepIndex;
    this.isActive = true;
    void this.showCurrentStep();
  }

  stop(): void {
    this.destroy();
  }

  next(): void {
    if (this.currentStepIndex < this.config.steps.length - 1) {
      this.currentStepIndex += 1;
      void this.showCurrentStep();
      return;
    }

    this.destroy();
  }

  back(): void {
    if (this.currentStepIndex === 0) {
      return;
    }

    this.currentStepIndex -= 1;
    void this.showCurrentStep();
  }

  destroy(): void {
    this.detachTooltip();
    this.detachBackdrop();
    this.currentStepIndex = 0;
    this.currentTargetRect = null;
    this.isActive = false;
  }

  isRunning(): boolean {
    return this.isActive;
  }

  private async showCurrentStep(): Promise<void> {
    if (!this.isActive) {
      return;
    }

    const step = this.config.steps[this.currentStepIndex];
    if (!step) {
      this.destroy();
      return;
    }

    // Add a short delay to smooth transitions between steps
    await new Promise((resolve) => setTimeout(resolve, 120));

    if (step.beforeShow) {
      await step.beforeShow();
    }

    const target = step.element
      ? await this.waitForElement(step.element, 3000)
      : null;

    if (target) {
      await this.scrollToTarget(target);
      this.currentTargetRect = target.getBoundingClientRect();
    } else {
      this.currentTargetRect = null;
    }

    this.attachBackdrop();
    this.attachTooltip(step, target);

    if (step.afterShow) {
      await step.afterShow();
    }
  }

  private attachBackdrop(): void {
    this.detachBackdrop();

    this.overlayRefBackdrop = this.overlay.create({
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically(),
      panelClass: "tour-cdk-backdrop",
    });

    const portal = new ComponentPortal(TourOverlayComponent);
    const componentRef = this.overlayRefBackdrop.attach(portal);
    componentRef.instance.targetRect = this.currentTargetRect;
  }

  private attachTooltip(
    step: TourStepConfig,
    target: HTMLElement | null,
  ): void {
    this.detachTooltip();

    const positionStrategy = target
      ? this.positionBuilder
          .flexibleConnectedTo(target)
          .withPositions(this.buildPositions(step.position))
      : this.overlay
          .position()
          .global()
          .centerHorizontally()
          .centerVertically();

    this.overlayRefTooltip = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: "tour-cdk-tooltip",
    });

    const portal = new ComponentPortal(TourTooltipComponent);
    const componentRef = this.overlayRefTooltip.attach(portal);
    componentRef.instance.step = step;
    componentRef.instance.currentStepIndex = this.currentStepIndex;
    componentRef.instance.stepCount = this.config.steps.length;
    componentRef.changeDetectorRef.detectChanges();

    this.observeTooltipLayout(target, componentRef, step.position);

    this.tooltipSubscription = componentRef.instance.buttonClick.subscribe(
      (button) => {
        void this.handleButtonClick(button);
      },
    );

    if (
      positionStrategy instanceof FlexibleConnectedPositionStrategy &&
      positionStrategy.positionChanges
    ) {
      this.tooltipPositionChangesSubscription =
        positionStrategy.positionChanges.subscribe(() => {
          this.scheduleTooltipLayoutRefresh(target, componentRef, step.position);
        });
    }
  }

  private observeTooltipLayout(
    target: HTMLElement | null,
    componentRef: ComponentRef<TourTooltipComponent>,
    fallbackPosition: TourStepConfig["position"],
  ): void {
    this.tooltipResizeObserver?.disconnect();
    this.tooltipResizeObserver = null;

    const overlayElement = this.overlayRefTooltip?.overlayElement;
    if (!overlayElement) {
      return;
    }

    const refreshLayout = () => {
      this.scheduleTooltipLayoutRefresh(target, componentRef, fallbackPosition);
    };

    this.tooltipResizeObserver = new ResizeObserver(() => refreshLayout());
    this.tooltipResizeObserver.observe(overlayElement);

    const tooltipElement = overlayElement.querySelector(".tour-tooltip");
    if (tooltipElement instanceof HTMLElement) {
      this.tooltipResizeObserver.observe(tooltipElement);
    }

    refreshLayout();
    window.requestAnimationFrame(() => refreshLayout());
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(refreshLayout);
    });
  }

  private scheduleTooltipLayoutRefresh(
    target: HTMLElement | null,
    componentRef: ComponentRef<TourTooltipComponent>,
    fallbackPosition: TourStepConfig["position"],
  ): void {
    if (this.tooltipLayoutRefreshRaf !== null) {
      cancelAnimationFrame(this.tooltipLayoutRefreshRaf);
    }

    this.tooltipLayoutRefreshRaf = window.requestAnimationFrame(() => {
      this.tooltipLayoutRefreshRaf = null;
      this.refreshTooltipLayout(target, componentRef, fallbackPosition);
    });
  }

  private refreshTooltipLayout(
    target: HTMLElement | null,
    componentRef: ComponentRef<TourTooltipComponent>,
    fallbackPosition: TourStepConfig["position"],
  ): void {
    if (!this.overlayRefTooltip) {
      return;
    }

    this.overlayRefTooltip.updatePosition();
    this.updateTooltipArrow(target, componentRef, fallbackPosition);
  }

  private detachBackdrop(): void {
    if (this.overlayRefBackdrop) {
      this.overlayRefBackdrop.dispose();
      this.overlayRefBackdrop = null;
    }
  }

  private detachTooltip(): void {
    if (this.tooltipLayoutRefreshRaf !== null) {
      cancelAnimationFrame(this.tooltipLayoutRefreshRaf);
      this.tooltipLayoutRefreshRaf = null;
    }

    this.tooltipResizeObserver?.disconnect();
    this.tooltipResizeObserver = null;

    if (this.tooltipSubscription) {
      this.tooltipSubscription.unsubscribe();
      this.tooltipSubscription = null;
    }

    if (this.tooltipPositionChangesSubscription) {
      this.tooltipPositionChangesSubscription.unsubscribe();
      this.tooltipPositionChangesSubscription = null;
    }

    if (this.overlayRefTooltip) {
      this.overlayRefTooltip.dispose();
      this.overlayRefTooltip = null;
    }
  }

  private async handleButtonClick(button: TourButtonConfig): Promise<void> {
    if (button.action === "next") {
      this.next();
      return;
    }

    if (button.action === "prev") {
      this.back();
      return;
    }

    if (button.action === "cancel") {
      // If it is a skip (class "tour-tooltip-skip"), run the onSkip callback
      if (button.classes?.includes("tour-tooltip-skip") && this.config.onSkip) {
        this.config.onSkip();
      }
      this.destroy();
      return;
    }

    if (button.action === "custom" && button.handler) {
      await button.handler();
    }
  }

  private buildPositions(
    position?: TourStepConfig["position"],
  ): ConnectedPosition[] {
    switch (position) {
      case "top":
        return [
          {
            originX: "center",
            originY: "top",
            overlayX: "center",
            overlayY: "bottom",
            offsetY: -24,
          },
          {
            originX: "center",
            originY: "bottom",
            overlayX: "center",
            overlayY: "top",
            offsetY: 24,
          },
        ];
      case "bottom":
        return [
          {
            originX: "center",
            originY: "bottom",
            overlayX: "center",
            overlayY: "top",
            offsetY: 24,
          },
          {
            originX: "center",
            originY: "top",
            overlayX: "center",
            overlayY: "bottom",
            offsetY: -24,
          },
        ];
      case "left":
        return [
          {
            originX: "start",
            originY: "center",
            overlayX: "end",
            overlayY: "center",
            offsetX: -20,
          },
          {
            originX: "end",
            originY: "center",
            overlayX: "start",
            overlayY: "center",
            offsetX: 20,
          },
        ];
      case "right":
        return [
          {
            originX: "end",
            originY: "center",
            overlayX: "start",
            overlayY: "center",
            offsetX: 20,
          },
          {
            originX: "start",
            originY: "center",
            overlayX: "end",
            overlayY: "center",
            offsetX: -20,
          },
        ];
      case "center":
        return [
          {
            originX: "center",
            originY: "center",
            overlayX: "center",
            overlayY: "center",
          },
        ];
      default:
        return [
          {
            originX: "center",
            originY: "bottom",
            overlayX: "center",
            overlayY: "top",
            offsetY: 20,
          },
          {
            originX: "center",
            originY: "top",
            overlayX: "center",
            overlayY: "bottom",
            offsetY: -20,
          },
        ];
    }
  }

  private updateTooltipArrow(
    target: HTMLElement | null,
    componentRef: ComponentRef<TourTooltipComponent>,
    fallbackPosition: TourStepPosition | undefined,
  ): void {
    if (!target || !this.overlayRefTooltip) {
      componentRef.instance.tooltipStyles = {};
      componentRef.changeDetectorRef.detectChanges();
      return;
    }

    const tooltipElement =
      this.overlayRefTooltip.overlayElement.querySelector(".tour-tooltip");
    const overlayRect =
      tooltipElement instanceof HTMLElement
        ? tooltipElement.getBoundingClientRect()
        : this.overlayRefTooltip.overlayElement.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const arrowHalf = 10;
    const actualPosition = this.getActualTooltipPosition(
      overlayRect,
      targetRect,
      fallbackPosition,
    );

    componentRef.instance.actualPosition = actualPosition;

    if (actualPosition === "bottom" || actualPosition === "top") {
      const targetCenter = targetRect.left + targetRect.width / 2;
      const left = Math.min(
        Math.max(targetCenter - overlayRect.left - arrowHalf, 12),
        overlayRect.width - arrowHalf * 2 - 12,
      );
      componentRef.instance.tooltipStyles = {
        "--tour-tooltip-arrow-left": `${left}px`,
      };
      componentRef.changeDetectorRef.detectChanges();
      return;
    }

    const targetMiddle = targetRect.top + targetRect.height / 2;
    const top = Math.min(
      Math.max(targetMiddle - overlayRect.top - arrowHalf, 12),
      overlayRect.height - arrowHalf * 2 - 12,
    );
    componentRef.instance.tooltipStyles = {
      "--tour-tooltip-arrow-top": `${top}px`,
    };
    componentRef.changeDetectorRef.detectChanges();
  }

  private getActualTooltipPosition(
    overlayRect: DOMRect,
    targetRect: DOMRect,
    stepPosition?: TourStepPosition,
  ): TourStepPosition {
    const tolerance = 2;

    if (overlayRect.bottom <= targetRect.top + tolerance) {
      return "top";
    }
    if (overlayRect.top >= targetRect.bottom - tolerance) {
      return "bottom";
    }
    if (overlayRect.right <= targetRect.left + tolerance) {
      return "left";
    }
    if (overlayRect.left >= targetRect.right - tolerance) {
      return "right";
    }

    return this.mapStepPositionToArrowClass(stepPosition);
  }

  /** Maps step placement config to tooltip arrow CSS class. */
  private mapStepPositionToArrowClass(
    stepPosition?: TourStepPosition,
  ): TourStepPosition {
    switch (stepPosition) {
      case "top":
        return "top";
      case "bottom":
        return "bottom";
      case "left":
        return "left";
      case "right":
        return "right";
      default:
        return "bottom";
    }
  }

  private async waitForElement(
    selector: string,
    timeoutMs = 3000,
    intervalMs = 100,
  ): Promise<HTMLElement | null> {
    const start = performance.now();

    while (performance.now() - start < timeoutMs) {
      const element = this.findElement(selector);
      if (element && element.offsetParent !== null) {
        return element;
      }
      await this.delay(intervalMs);
    }

    return null;
  }

  private findElement(selector: string): HTMLElement | null {
    if (selector.startsWith("by-text:")) {
      const searchText = selector.replace("by-text:", "");
      return this.findElementByText(searchText);
    }

    return document.querySelector(selector) as HTMLElement | null;
  }

  private findElementByText(text: string): HTMLElement | null {
    const elements = Array.from(document.querySelectorAll(".menu-list-item"));
    return elements.find((el) =>
      el.textContent?.includes(text),
    ) as HTMLElement | null;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async scrollToTarget(target: HTMLElement): Promise<void> {
    const scrollParent = this.getScrollParent(target);
    target.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
    await this.waitForScrollEnd(scrollParent);
    await this.waitForTargetVisible(target, scrollParent);
    await this.delay(100);
  }

  private waitForScrollEnd(
    scrollParent: HTMLElement | Window | null = window,
    timeoutMs = 1000,
    idleThresholdMs = 120,
  ): Promise<void> {
    const start = performance.now();
    let lastScrollTop = this.getScrollTop(scrollParent);
    let lastScrollLeft = this.getScrollLeft(scrollParent);
    let lastChangeTime = start;

    return new Promise((resolve) => {
      const check = () => {
        const currentScrollTop = this.getScrollTop(scrollParent);
        const currentScrollLeft = this.getScrollLeft(scrollParent);

        const hasMoved =
          currentScrollTop !== lastScrollTop ||
          currentScrollLeft !== lastScrollLeft;

        if (hasMoved) {
          lastScrollTop = currentScrollTop;
          lastScrollLeft = currentScrollLeft;
          lastChangeTime = performance.now();
        }

        const elapsedSinceChange = performance.now() - lastChangeTime;
        const elapsedSinceStart = performance.now() - start;

        if (
          elapsedSinceChange >= idleThresholdMs ||
          elapsedSinceStart >= timeoutMs
        ) {
          resolve();
          return;
        }

        requestAnimationFrame(check);
      };

      check();
    });
  }

  private async waitForTargetVisible(
    target: HTMLElement,
    scrollParent: HTMLElement | Window | null,
    timeoutMs = 1000,
    intervalMs = 16,
  ): Promise<void> {
    const start = performance.now();

    const isVisible = (): boolean => {
      const targetRect = target.getBoundingClientRect();
      if (scrollParent === window || scrollParent === null) {
        return (
          targetRect.top >= 0 &&
          targetRect.left >= 0 &&
          targetRect.bottom <= window.innerHeight &&
          targetRect.right <= window.innerWidth
        );
      }

      const parentRect = (scrollParent as HTMLElement).getBoundingClientRect();
      return (
        targetRect.top >= parentRect.top &&
        targetRect.left >= parentRect.left &&
        targetRect.bottom <= parentRect.bottom &&
        targetRect.right <= parentRect.right
      );
    };

    return new Promise((resolve) => {
      const check = () => {
        if (isVisible()) {
          resolve();
          return;
        }

        if (performance.now() - start >= timeoutMs) {
          resolve();
          return;
        }

        setTimeout(check, intervalMs);
      };

      check();
    });
  }

  private getScrollTop(scrollParent: HTMLElement | Window | null): number {
    if (!scrollParent) {
      return 0;
    }
    return scrollParent === window
      ? window.scrollY
      : (scrollParent as HTMLElement).scrollTop;
  }

  private getScrollLeft(scrollParent: HTMLElement | Window | null): number {
    if (!scrollParent) {
      return 0;
    }
    return scrollParent === window
      ? window.scrollX
      : (scrollParent as HTMLElement).scrollLeft;
  }

  private getScrollParent(element: HTMLElement): HTMLElement | Window | null {
    let parent: HTMLElement | null = element.parentElement;

    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      const overflowX = style.overflowX;
      const isScrollableY = overflowY !== "visible" && overflowY !== "hidden";
      const isScrollableX = overflowX !== "visible" && overflowX !== "hidden";

      if (
        (isScrollableY && parent.scrollHeight > parent.clientHeight) ||
        (isScrollableX && parent.scrollWidth > parent.clientWidth)
      ) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return window;
  }
}
